/**
 * Copyright (c) Qwoach LLC 2025. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Created by Artem Bogush <artem@qwoach.com> on 28.01.25
 *
 */
import BaseObject from "@shared/BaseObject.js";
import {
	createAuxNode, GenogramEnum, getData, getPosition, nonempty, setPosition
} from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/lib/genogramUtils";
import {uuidv4} from "@/utils/helpers";
import {
	getPartnerGender
} from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/defaults/node-types";

let BASE_COUPLE_DISTANCE
let BASE_GEN_DISTANCE_H
let BASE_GEN_DISTANCE_V
let BASE_SIBLING_DISTANCE_H

class GenogramTemplateManager extends BaseObject {

	cy = null;

	_options = {
		nodeTypes: [],
		nodeTypeSymbols: [],
		gridSize: 35,
	};


	constructor(cy, params = {}) {
		super();
		this.cy = cy;
		this._options = {
			...this._options,
			...params
		};
		let emptyFn = () => {}
		this.notifyAction = params.notifyAction || emptyFn
		if(this._options.nodeTypeSymbols.length === 0){
			this._options.nodeTypeSymbols = this._options.nodeTypes.filter(it => it.category === "Symbols")
		}

		BASE_COUPLE_DISTANCE = this._options.gridSize * 4
		BASE_GEN_DISTANCE_H = this._options.gridSize * 6
		BASE_SIBLING_DISTANCE_H = this._options.gridSize * 4
		BASE_GEN_DISTANCE_V = this._options.gridSize * 8

		console.log("GenogramTemplateManager: constructor: this._options = ", this._options)
	}

	async fromTemplateToElements(templateData){

		switch (templateData.data.type) {
			case "template-generations":
				return this.fromTemplateGenerationsToElement(templateData)
			case "template-pair":
				return this.fromTemplateCoupleToElement(templateData)
		}
		return templateData
	}

	async fromTemplateGenerationsToElement(templateData){
		let numberOfGenerations = await this.notifyAction('getTemplateGenerationsParams')

		if(numberOfGenerations === false){
			return
		}

		let {position} = templateData

		//generate a tree of parents based on the numberOfGenerations

		return this.generateParentGenerations({position, numberOfGenerations})
	}

	generateParentGenerations({position, numberOfGenerations, childNode}){
		console.log("GenogramTemplateManager: generateParentGenerations: position = ", position, ", numberOfGenerations = ", numberOfGenerations, ", childNode = ", childNode)
		if(!position && !childNode){
			console.warn("GenogramTemplateManager: generateParentGenerations: position or childNode should be provided")
			return
		}
		let nodsAndEdges = []
		let connectionElems = []




		position = position || getPosition(childNode)
		let numberOfCouples = 1
		let generationY = childNode ? position.y - BASE_GEN_DISTANCE_V : position.y

		console.log("GenogramTemplateManager: generateParentGenerations: position = ", position, ", numberOfGenerations = ", numberOfGenerations, ", childNode = ", childNode, ", generationY = ", generationY)

		for(let i = 0; i < numberOfGenerations; i++) {
			let coupleDistance = BASE_COUPLE_DISTANCE * Math.pow(2, numberOfGenerations - i)
			for (let j = 0; j < numberOfCouples; j++) {

				let middlePointX = this.calculateCoupleX({
					x0: position.x,
					coupleIndex: j,
					level: i,
					baseWidth: BASE_COUPLE_DISTANCE,
					pairSpacing: BASE_GEN_DISTANCE_H,
					numberOfGenerations,
				})

				let leftX = middlePointX - coupleDistance / 2
				let rightX = middlePointX + coupleDistance / 2

				let leftNode = this.createMaleNode({
					position: {
						x: leftX,
						y: generationY
					},
				})

				let rightNode = this.createFemaleNode({
					type: "male",
					position: {
						x: rightX,
						y: generationY
					},
				})

				let marriageEdge = this.createMarriageEdge({
					sourceId: getData(leftNode).id,
					targetId: getData(rightNode).id,
				})



				if(i > 0){
					let childIndex = this.findChildIndex(j, i)
					let childNode = nodsAndEdges[childIndex]

					let auxNode = createAuxNode({
						parentEdge: marriageEdge,
						childNode,
						x: getPosition(childNode).x,
					})

					let connectionEdge = this.createChildEdge({
						sourceId: getData(childNode).id,
						targetId: getData(auxNode).id,
					})

					connectionElems.push(auxNode, connectionEdge)

				}

				nodsAndEdges.push(leftNode, rightNode)
				connectionElems.push(marriageEdge)

			}
			numberOfCouples *= 2
			generationY -= BASE_GEN_DISTANCE_V
		}




		if(childNode){
			let auxNode = createAuxNode({
				parentEdge: connectionElems[0],
				childNode,
				x: getPosition(childNode).x,
			})
			let connectionEdge = this.createChildEdge({
				sourceId: getData(childNode).id,
				targetId: getData(auxNode).id,
			})
			connectionElems.push(auxNode, connectionEdge)
		}


		nodsAndEdges.push(...connectionElems)

		return nodsAndEdges
	}


	calculateCoupleX({x0, coupleIndex, level, baseWidth, pairSpacing, numberOfGenerations}) {
		let W0 = (baseWidth + pairSpacing) * (2 ** numberOfGenerations - 1);
		let W_level = W0 / (2 ** level);
		return x0 + (coupleIndex - (2 ** level - 1) / 2) * W_level;
	}

	findChildIndex(pairIndex, level) {
		if (level === 0) return null; // No parent for the root node
		return (2 ** (level) - 1) + pairIndex - 1;
	}

	// calculateCoupleX({x0, generationLevel, coupleIndex, numberOfGenerations, baseCoupleDistance, interCoupleDistance}) {
	// 	// Calculate the scaling factor
	// 	const scalingFactor = Math.pow(2, numberOfGenerations - generationLevel);
	//
	// 	// Calculate the spacing between couples
	// 	const spacing = baseCoupleDistance * scalingFactor + interCoupleDistance// * (Math.pow(generationLevel, 2) - 1);
	//
	// 	// Calculate the center offset
	// 	const centerOffset = (coupleIndex - (Math.pow(2, generationLevel) - 1) / 2) * spacing;
	//
	// 	// Calculate the final x-coordinate
	// 	const x = x0 + centerOffset;
	//
	// 	return x;
	// }

	async addKids(initialNode){

		let existingPartners = this.findPartners(initialNode)

		let params = {
			existingPartners,
		}
		let kidsMap = await this.notifyAction('getTemplateKidsParams', params);

		console.log("GenogramTemplateManager: addKids: initialNode = ", initialNode, ", kidsMap = ", kidsMap, ", params = ", params)

		if(!kidsMap){
			return
		}

		let newElements = []

		let {partnerNode, marriageEdge} = this.setupPartnerFromKidsMap(kidsMap, initialNode, newElements)
		let mostRightKidX = this.setMinimalDistanceBetweenPartners(initialNode, partnerNode, marriageEdge, kidsMap)

		let kidsElems = this.createKidsElems({
			kidsMap,
			parent1: initialNode,
			parent2: partnerNode,
			leftX: mostRightKidX,
			marriageEdge
		})

		newElements.push(...kidsElems);

		console.log("GenogramTemplateManager: addKids: newElements = ", newElements)
		return {
			newElements,
			partners: [initialNode, partnerNode],
			marriageEdge,
		}
	}

	setMinimalDistanceBetweenPartners(node1, node2, familyEdge, kidsMap, existingKids = []){
		console.log("GenogramTemplateManager: setMinimalDistanceBetweenPartners: node1 = ", node1, ", node2 = ", node2, ", familyEdge = ", familyEdge, ", kidsMap = ", kidsMap)
		let node1X = getPosition(node1).x
		let node2X = getPosition(node2).x

		let leftNode = node1X < node2X ? node1 : node2
		let rightNode = leftNode === node1 ? node2 : node1

		let mostRightKidX = getPosition(leftNode).x

		console.log("GenogramTemplateManager: setMinimalDistanceBetweenPartners: mostRightKidX = ", mostRightKidX, ", leftNode = ", leftNode, ", rightNode = ", rightNode, ", familyEdge = ", familyEdge)

		let auxNodes = getData(familyEdge).auxNodes || []
		let hasKids = existingKids.length > 0

		let existingKidsLocal = [
			...existingKids.filter(node => nonempty(node)),
			...auxNodes.map(nodeId => this.cy.getElementById(nodeId)).filter(node => node.nonempty())
		]
		for(let kidNode of existingKidsLocal){
			let auxNodeX = getPosition(kidNode).x
			if(auxNodeX > mostRightKidX){
				mostRightKidX = auxNodeX
				hasKids = true
			}
		}


		let kidsCount = Object.keys(kidsMap).reduce((acc, key) => acc + ((typeof kidsMap[key] === "number") ? kidsMap[key] : 0), 0)
		let minKidsCount = hasKids ? 0 : 2
		let distance = BASE_SIBLING_DISTANCE_H * Math.max(minKidsCount, kidsCount - 1) + BASE_SIBLING_DISTANCE_H/2
		if(hasKids){
			mostRightKidX += BASE_SIBLING_DISTANCE_H
		} else {
			distance += BASE_SIBLING_DISTANCE_H / 2
		}

		console.log("GenogramTemplateManager: setMinimalDistanceBetweenPartners: mostRightKidX = ", mostRightKidX, ", distance = ", distance, ", kidsCount = ", kidsCount, ", hasKids = ", hasKids)
		let rightNodeX = mostRightKidX + distance
		let rightNodePositionX = getPosition(rightNode).x
		if(rightNodeX > rightNodePositionX) {
			setPosition(rightNode, {x: rightNodeX})
		}

		return mostRightKidX

	}

	getCoupleFamilyEdge(node1, node2){
		let thisFamilyCoupleEdge

		let edges = node1.connectedEdges()
		let node2Id = node2.id()

		for(let edge of edges){

			let source = edge.source()
			let target = edge.target()
			let lineSubType = edge.data("lineSubType")

			let isThisCoupleEdge = source.id() === node2Id || target.id() === node2Id
			let isFamilyEdge = lineSubType && lineSubType.startsWith("family")

			if(isThisCoupleEdge && isFamilyEdge){
				thisFamilyCoupleEdge = edge
				break
			}
		}

		return thisFamilyCoupleEdge
	}

	setupPartnerFromKidsMap(kidsMap, initialNode, newElements){
		let partnerNode
		let marriageEdge
		if(kidsMap.partner === GenogramEnum.CREATE_PARTNER){
			let position = getPosition(initialNode)
			partnerNode = this.createNode({
				type: getPartnerGender(getData(initialNode).type),
				position: {
					x: position.x + this._options.gridSize * 4,
					y: position.y
				},
			})
			marriageEdge = this.createMarriageEdge({
				sourceId: getData(initialNode).id,
				targetId: getData(partnerNode).id,
			})

			newElements.push(partnerNode, marriageEdge)

		} else {
			partnerNode = this.cy.getElementById(kidsMap.partner)
			marriageEdge = this.getCoupleFamilyEdge(initialNode, partnerNode)
		}
		return {
			partnerNode,
			marriageEdge,
		}
	}

	async addParents(initialNode){
		let hasParents = initialNode.neighborhood().nodes().filter(node => node.data().isAuxNode).length > 0
		if(hasParents){
			await this.notifyAction('showMessage', {
				title: "Parents already exist",
				message: "Parents already exist for this person",
				yesHide: true,
			})
			return
		}
		let numberOfGenerations = await this.notifyAction('getTemplateGenerationsParams')
		console.log("GenogramTemplateManager: addParents: initialNode = ", initialNode, ", numberOfGenerations = ", numberOfGenerations)

		if(numberOfGenerations === false){
			return
		}

		return this.generateParentGenerations({
			childNode: initialNode,
			numberOfGenerations
		})
	}

	async addSiblings(initialNode){
		let params = {

		}
		let kidsMap = await this.notifyAction('getTemplateKidsParams', params);

		console.log("GenogramTemplateManager.addSiblings: initialNode = ", initialNode, ", kidsMap = ", kidsMap, ", params = ", params)

		if(!kidsMap){
			return
		}

		let newElements = []

		let {parent1, parent2, marriageEdge} = this.findOrCreateParents(initialNode, newElements)

		let mostRightKidX = this.setMinimalDistanceBetweenPartners(parent1, parent2, marriageEdge, kidsMap, [initialNode])

		let kidsElems = this.createKidsElems({
			kidsMap,
			parent1,
			parent2,
			leftX: mostRightKidX,
			marriageEdge
		})

		newElements.push(...kidsElems);

		console.log("GenogramTemplateManager.addSiblings: newElements = ", newElements)
		return {
			newElements,
			partners: [parent1, parent2],
			marriageEdge,
		}
	}

	findOrCreateParents(node, newElements){
		let auxNodes = node.neighborhood().nodes().filter(node => node.data().isAuxNode)

		let parent1
		let parent2
		let marriageEdge

		if(auxNodes.length > 0){
			let auxNode = auxNodes[0]
			let parentEdge = this.cy.getElementById(getData(auxNode).parentEdgeId)
			if(parentEdge.nonempty()){
				parent1 = parentEdge.source()
				parent2 = parentEdge.target()
				marriageEdge = parentEdge
			}
		}

		if(!parent1 || !parent2 || !marriageEdge){
			parent1 = this.createMaleNode({
				position: {
					x: getPosition(node).x - BASE_COUPLE_DISTANCE / 2,
					y: getPosition(node).y - BASE_GEN_DISTANCE_V,
				}
			})
			parent2 = this.createFemaleNode({
				position: {
					x: getPosition(node).x + BASE_COUPLE_DISTANCE / 2,
					y: getPosition(node).y - BASE_GEN_DISTANCE_V,
				}
			})
			marriageEdge = this.createMarriageEdge({
				sourceId: getData(parent1).id,
				targetId: getData(parent2).id,
			})
			let auxNode = createAuxNode({
				parentEdge: marriageEdge,
				childNode: node,
				x: getPosition(node).x,
			})
			let connectionEdge = this.createChildEdge({
				sourceId: getData(node).id,
				targetId: getData(auxNode).id,
			})
			newElements.push(parent1, parent2, marriageEdge, auxNode, connectionEdge)
		}

		return {parent1, parent2, marriageEdge}

	}

	findPartners(node){
		let edges = node.connectedEdges()
		let partners = []
		for(let edge of edges){
			let lineSubType = edge.data("lineSubType") || ""
			if(lineSubType.startsWith("family")){
				let source = edge.source()
				let target = edge.target()
				if (source.id() === node.id()) {
					partners.push(target)
				} else if (target.id() === node.id()) {
					partners.push(source)
				}
			}		}
		return partners
	}

	async fromTemplateCoupleToElement(templateData) {
		let kidsMap = await this.notifyAction('getTemplateCoupleParams');
		console.log("GenogramTemplateManager: fromTemplateCoupleToElement: kidsMap = ", kidsMap)
		if(!kidsMap){
			return
		}
		let { position } = templateData;


		let numberOfKids = Object.keys(kidsMap).reduce((acc, key) => acc + kidsMap[key], 0);
		let minDistance = this._options.gridSize * (Math.max(2, numberOfKids) * 4);

		let node1 = this.createMaleNode({
			position: {
				x: position.x - minDistance/2,
				y: position.y
			},
			data: templateData.data
		});

		let node2 = this.createFemaleNode({
			position: {
				x: position.x + minDistance/2,
				y: position.y
			},
			data: templateData.data
		})

		let marriageEdge = this.createMarriageEdge({
			sourceId: node1.data.id,
			targetId: node2.data.id,
		});

		let kidsElems = this.createKidsElems({
			kidsMap,
			parent1: node1,
			parent2: node2,
			marriageEdge
		})

		let elems = [node1, node2, marriageEdge, ...kidsElems];
		console.log("GenogramTemplateManager: fromTemplateCoupleToElement: elems = ", elems)
		return elems
	}

	createKidsElems(params = {}){

		console.log("GenogramTemplateManager: createKidsElems: params = ", params)
		let {kidsMap, parent1, parent2, marriageEdge, leftX} = params

		let kidNodeTypes = [];
		let kidsKeys = Object.keys(kidsMap);

		let auxNodes = getData(marriageEdge).auxNodes || []
		let hasExistingKids = auxNodes.length > 0

		let kidsCount = kidsKeys.reduce((acc, key) => acc + (typeof kidsMap[key] === "number" ? kidsMap[key] : 0), 0);

		let position1 = getPosition(parent1)
		let position2 = getPosition(parent2)

		let xLeft = leftX || Math.min(position1.x, position2.x)
		let xRight = Math.max(position1.x, position2.x)
		let distance = xRight - xLeft
		let y = Math.max(position1.y, position2.y)

		let leftMargin = hasExistingKids && leftX ? 0 : distance / kidsCount / 2

		let kidIndex = 0
		for(let i = 0; i < kidsKeys.length; i++){
			let kidKey = kidsKeys[i];
			let kidCount = kidsMap[kidKey];
			if (typeof kidCount !== "number") {
				continue
			}
			for(let j = 0; j < kidCount; j++){
				let xShift = leftMargin + BASE_SIBLING_DISTANCE_H * kidIndex
				let newX = xLeft + xShift

				let kidNode = this.createNode({
					type: kidKey,
					position: {
						x: newX,
						y: y + BASE_GEN_DISTANCE_V,
					},
				})

				let auxNode = createAuxNode({
					parentEdge: marriageEdge,
					childNode: kidNode,
					x: newX,
					xPositionPercent: xShift / distance,
				})

				let childEdge = this.createChildEdge({
					sourceId: getData(kidNode).id,
					targetId: getData(auxNode).id,
				})

				kidNodeTypes.push(kidNode, auxNode, childEdge);

				kidIndex++
			}
			// console.log("GenogramTemplateManager: createKidsElems: kids = ", kids)

		}

		return kidNodeTypes

	}


	createMaleNode(options = {}) {
		return this.createNode({
			...options,
			type: "male",
		})
	}

	createFemaleNode(options = {}) {
		return this.createNode({
			...options,
			type: "female",
		})
	}

	// Function to create a single node
	createNode({id, type, position, data = {}}) {
		return {
			data: {
				...data,
				id: id || uuidv4(),
				type: type,
			},
			group: 'nodes',
			position: position,
		};
	}

	createChildEdge(options = {}) {
		return this.createEdge({
			...options,
			lineType: "bezier",
			lineSubType: "straight",
		})
	}
	createMarriageEdge(options = {}) {
		return this.createEdge({
			...options,
			lineType: "segments",
			lineSubType: "family-marriage",
		})
	}

// Function to create an edge between two nodes
	createEdge({sourceId, targetId, edgeId, lineSubType = "", lineType=""}) {
		return {
			data: {
				lineType,
				lineSubType,
				source: sourceId,
				target: targetId,
				id: edgeId || uuidv4(),
			},
			group: 'edges',
		};
	}
}


export default (cytoscape) => {
	if (!cytoscape) {
		return
	}

	cytoscape('core', 'templates', function (params) {
		return new GenogramTemplateManager(this, params)
	})
}
